import * as React from "react"
import { getStudentAttendance } from "../auth/api"

const WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"]
const MONTHS_PT = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
]

function pad(n) {
  return String(n).padStart(2, "0")
}

function toDateString(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function MonthGrid({ year, month, presentDates }) {
  const today = toDateString(new Date())
  const firstWeekday = new Date(year, month, 1).getDay()
  const totalDays = new Date(year, month + 1, 0).getDate()

  const cells = []
  for (let i = 0; i < firstWeekday; i++) cells.push(null)
  for (let day = 1; day <= totalDays; day++) {
    cells.push(new Date(year, month, day))
  }

  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 text-center">
        {MONTHS_PT[month]}
      </p>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {WEEKDAYS.map((w, idx) => (
          <div
            key={idx}
            className="text-[10px] font-semibold text-slate-400 pb-0.5">
            {w}
          </div>
        ))}
        {cells.map((date, idx) => {
          if (!date) return <div key={idx} />
          const dateStr = toDateString(date)
          const isPresent = presentDates.has(dateStr)
          const isToday = dateStr === today

          return (
            <div
              key={idx}
              className={`aspect-square rounded text-[11px] font-semibold flex items-center justify-center ${
                isPresent
                  ? "bg-cor-base text-slate-100"
                  : isToday
                    ? "border border-cor-base text-cor-base"
                    : "text-slate-400"
              }`}>
              {date.getDate()}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function StudentAttendanceModal({ student, onClose }) {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = React.useState(currentYear)
  const [data, setData] = React.useState({ total: 0, dates: [] })
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState("")

  React.useEffect(() => {
    let mounted = true
    setLoading(true)
    setError("")
    getStudentAttendance(student.id, year)
      .then((res) => {
        if (mounted) setData(res)
      })
      .catch((err) => {
        if (mounted) setError(err?.message || "Erro ao carregar presenças.")
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [student.id, year])

  React.useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose()
  }

  const presentDates = React.useMemo(() => new Set(data.dates), [data.dates])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md rounded-2xl p-4"
      onClick={handleBackdropClick}>
      <div className="relative w-full max-w-2xl max-h-[70vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-cor-base rounded-t-2xl">
          <div className="min-w-0">
            <h2 className="text-xl font-oswald font-semibold text-white tracking-wide break-words">
              Presenças — {student.nomeCompleto}
            </h2>
            <p className="text-white/70 text-sm">
              Matrícula {student.matricula} · {student.graduacao}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white text-2xl leading-none transition-colors ml-4 shrink-0"
            aria-label="Fechar"
            type="button">
            ×
          </button>
        </div>

        <div className="px-6 py-5">
          {/* Seletor de ano */}
          <div className="flex items-center justify-center gap-4 mb-5">
            <button
              type="button"
              onClick={() => setYear((y) => y - 1)}
              className="px-3 py-1.5 rounded-lg border-2 border-cor-base text-cor-base font-semibold hover:scale-[1.05] transition-all">
              ←
            </button>
            <span className="font-semibold text-slate-900 text-lg">{year}</span>
            <button
              type="button"
              onClick={() => setYear((y) => y + 1)}
              disabled={year >= currentYear}
              className="px-3 py-1.5 rounded-lg border-2 border-cor-base text-cor-base font-semibold hover:scale-[1.05] transition-all disabled:opacity-30 disabled:cursor-not-allowed">
              →
            </button>
          </div>

          {/* Total do ano */}
          {!loading && !error && (
            <p className="text-center text-2xl font-semibold text-cor-base mb-6">
              {data.total}{" "}
              <span className="text-sm font-normal text-slate-500">
                {data.total === 1 ? "presença em" : "presenças em"} {year}
              </span>
            </p>
          )}

          {error && <p className="text-red-600 text-center mb-4">{error}</p>}

          {loading ? (
            <p className="text-slate-500 text-center py-8">Carregando...</p>
          ) : (
            /* Grade de 12 meses */
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
              {Array.from({ length: 12 }, (_, i) => (
                <MonthGrid
                  key={i}
                  year={year}
                  month={i}
                  presentDates={presentDates}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
